import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

/**
 * Ein Eingabefeld mit einer Vorschlagsliste, die man auch sieht.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum nicht mehr <datalist>
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Bis hierher hingen die Ortsvorschläge an einem `<datalist>`. Die Begründung
 * dafür stand im Quelltext und klang gut: der Browser kenne die Bedienung
 * seiner eigenen Liste besser, als wir sie nachbauen könnten.
 *
 * Gemeldet wurde dann: «Ich gebe zwei Zahlen an und es kommt was, nur wird es
 * nicht angezeigt.» Auf dem Bildschirmfoto stand die Liste da – 5000 Aarau,
 * 5001 Aarau 1, 5004 Aarau – in einem Grau, das man gegen Weiss praktisch
 * nicht lesen kann.
 *
 * Und daran lässt sich nichts ändern. Das Aufklappmenü eines `<datalist>`
 * malt der Browser selbst, ausserhalb der Seite; es nimmt keine einzige
 * CSS-Regel an. Wie es aussieht, entscheiden Betriebssystem und
 * Browserversion – bei jedem Kunden also anders, und bei manchen unlesbar.
 *
 * Für eine Nebensächlichkeit wäre das hinnehmbar. Die Ortssuche ist keine:
 * Sie ist der Grund, warum niemand mehr eine Postleitzahl nachschlagen muss.
 * Ein Bedienelement, das diese Rolle trägt, darf nicht davon abhängen, wie
 * ein fremder Hersteller heute Grau definiert.
 *
 * Deshalb hier eine eigene Liste. Was der Browser sonst mitbringt, steht
 * damit in unserer Verantwortung, und zwar vollständig:
 *
 *   ↑ ↓      durch die Vorschläge
 *   Enter    übernehmen
 *   Esc      schliessen, ohne zu übernehmen
 *   Tab      schliessen und weiter
 *   Klick    übernehmen (auf mousedown, nicht auf click – sonst schliesst
 *            der Fokuswechsel die Liste weg, bevor der Klick ankommt)
 *
 * Dazu die Rollen für Bildschirmleser (combobox / listbox / option) und
 * aria-activedescendant, damit dort angesagt wird, was gerade markiert ist.
 */

export interface Vorschlag {
  /** Was ins Feld geschrieben wird. */
  wert: string
  /** Fett in der Zeile – meistens dasselbe wie der Wert. */
  haupt: string
  /** Blasser daneben – der Zusatz, der die Zeile erklärt. */
  neben?: string
  /** Eindeutig innerhalb der Liste. */
  schluessel: string
}

export interface VorschlagsfeldProps {
  id: string
  wert: string
  aufWert: (wert: string) => void
  /** Wird zusätzlich gerufen, wenn der Wert AUS DER LISTE kommt. */
  aufAuswahl?: (v: Vorschlag) => void
  vorschlaege: ReadonlyArray<Vorschlag>
  placeholder?: string
  inputMode?: 'text' | 'numeric' | 'decimal'
  maxLength?: number
  disabled?: boolean
  className?: string
  /** Steht unter dem Feld, solange die Liste zu ist. */
  hinweis?: ReactNode
  /**
   * Ab wie vielen getippten Zeichen die Liste erscheint.
   *
   * Vorher klappte sie beim blossen Hineinklicken auf – mit allen Vorschlägen,
   * die es gibt. Wer ein bereits ausgefülltes Feld nur antippt, um den Cursor
   * zu setzen, bekam eine Liste über den Bildschirm, und auf dem Handy
   * verdeckte sie die Felder darunter.
   *
   * Eins ist die Vorgabe, weil schon ein Zeichen die Liste brauchbar kürzt.
   */
  mindestlaenge?: number
}

export function Vorschlagsfeld({
  id,
  wert,
  aufWert,
  aufAuswahl,
  vorschlaege,
  placeholder,
  inputMode = 'text',
  maxLength,
  disabled,
  className,
  hinweis,
  mindestlaenge = 1
}: VorschlagsfeldProps): JSX.Element {
  const [offen, setOffen] = useState(false)
  /* Mit Pfeil-nach-unten geöffnet – dann gilt die Mindestlänge nicht. */
  const [tastaturOffen, setTastaturOffen] = useState(false)
  const [markiert, setMarkiert] = useState(-1)
  const huelle = useRef<HTMLDivElement>(null)
  const listenId = useId()

  // Ein Klick irgendwo sonst schliesst die Liste. Ohne das bliebe sie offen
  // stehen, während der Betrieb längst im nächsten Feld tippt.
  useEffect(() => {
    if (!offen) return
    const zu = (e: MouseEvent): void => {
      if (!huelle.current?.contains(e.target as Node)) {
        setOffen(false)
        setTastaturOffen(false)
      }
    }
    document.addEventListener('mousedown', zu)
    return () => document.removeEventListener('mousedown', zu)
  }, [offen])

  // Ändert sich die Liste unter der Markierung weg, zeigt die Markierung
  // sonst auf eine Zeile, die es nicht mehr gibt.
  useEffect(() => {
    setMarkiert((m) => (m >= vorschlaege.length ? -1 : m))
  }, [vorschlaege.length])

  /*
    Die Liste erscheint beim TIPPEN, nicht beim Hineinklicken.

    `offen` sagt, ob sie erscheinen darf; die Länge sagt, ob sie soll. Über die
    Tastatur bleibt sie trotzdem erreichbar: Pfeil-nach-unten öffnet sie auch
    ohne Eingabe – wer keine Maus benutzt, kommt sonst nicht an die Vorschläge.
  */
  const genugGetippt = wert.trim().length >= mindestlaenge
  const zeigen = offen && vorschlaege.length > 0 && (genugGetippt || tastaturOffen)

  function uebernimm(v: Vorschlag): void {
    aufWert(v.wert)
    aufAuswahl?.(v)
    setOffen(false)
    setTastaturOffen(false)
    setMarkiert(-1)
  }

  return (
    <div className="rp-vf" ref={huelle}>
      <input
        autoComplete="off"
        /*
          Der Browser soll hier nichts eigenes beisteuern. `off` allein reicht
          bei Chrome nicht immer; die drei zusammen schon – und sie sind der
          Grund, warum in diesem Feld keine fremde Adresse mehr auftaucht.
        */
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={zeigen}
        aria-controls={listenId}
        aria-autocomplete="list"
        aria-activedescendant={markiert >= 0 ? `${listenId}-${markiert}` : undefined}
        id={id}
        className={className}
        inputMode={inputMode}
        maxLength={maxLength}
        disabled={disabled}
        placeholder={placeholder}
        value={wert}
        onChange={(e) => {
          aufWert(e.target.value)
          setOffen(true)
          setMarkiert(-1)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOffen(false)
            setTastaturOffen(false)
            setMarkiert(-1)
            return
          }
          if (e.key === 'Tab') {
            setOffen(false)
            setTastaturOffen(false)
            return
          }
          if (!zeigen) {
            if (e.key === 'ArrowDown') {
              setOffen(true)
              setTastaturOffen(true)
            }
            return
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setMarkiert((m) => (m + 1) % vorschlaege.length)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setMarkiert((m) => (m <= 0 ? vorschlaege.length - 1 : m - 1))
          } else if (e.key === 'Enter' && markiert >= 0) {
            // Nur abfangen, wenn wirklich etwas markiert ist – sonst kann ein
            // Formular nicht mehr mit Enter abgeschickt werden.
            e.preventDefault()
            uebernimm(vorschlaege[markiert])
          }
        }}
      />
      {zeigen && (
        <ul className="rp-vf-liste" id={listenId} role="listbox">
          {vorschlaege.map((v, i) => (
            <li
              key={v.schluessel}
              id={`${listenId}-${i}`}
              role="option"
              aria-selected={i === markiert}
              className={i === markiert ? 'rp-vf-zeile rp-vf-markiert' : 'rp-vf-zeile'}
              onMouseEnter={() => setMarkiert(i)}
              onMouseDown={(e) => {
                // mousedown und nicht click: click käme erst nach dem
                // Fokusverlust, und der hat die Liste da schon geschlossen.
                e.preventDefault()
                uebernimm(v)
              }}
            >
              <span className="rp-vf-haupt">{v.haupt}</span>
              {v.neben && <span className="rp-vf-neben">{v.neben}</span>}
            </li>
          ))}
        </ul>
      )}
      {hinweis && !zeigen && <span className="rp-vf-hinweis">{hinweis}</span>}
    </div>
  )
}
